import { createLogger } from "@/logger";

const log = createLogger("reset");

import { idx, store } from "@/store/store";

export type DeletionResult = {
  deletedItems: number;
  deletedContentIds: number;
  affectedFeeds: number;
};

export async function deleteItemsAndOrphanedContent(
  itemIds: string[],
  feedIds: string[],
  label: string,
): Promise<DeletionResult> {
  log.debug(`[${label}] Deleting ${itemIds.length} items from ${feedIds.length} feeds`);

  const itemIdSet = new Set(itemIds);

  // Content ids the deleted items referenced, minus any still referenced by surviving items.
  const candidateContentIds = new Set<string>();
  for (const itemId of itemIds) {
    const item = store.get("item", itemId);
    if (item) candidateContentIds.add(item.contentId);
  }
  const stillReferenced = new Set<string>();
  for (const item of store.getAll("item").values()) {
    if (itemIdSet.has(item.id)) continue;
    if (candidateContentIds.has(item.contentId)) stillReferenced.add(item.contentId);
  }
  const orphanedContentIds = [...candidateContentIds].filter((contentId) => !stillReferenced.has(contentId));

  store.batch(() => {
    store.delete("item", itemIds);
    store.deleteWhere("accountEvent", (event) => event.itemId != null && itemIdSet.has(event.itemId));

    if (orphanedContentIds.length > 0) {
      log.debug(`[${label}] Deleting ${orphanedContentIds.length} orphaned content records`);
      for (const contentId of orphanedContentIds) {
        store.delete("transcription", [...idx.transcriptionsByContent.keys(contentId)]);
        store.delete("audioEncoding", [...idx.audioEncodingsByContent.keys(contentId)]);
        store.delete("displayArtifact", [...idx.displayArtifactsByContent.keys(contentId)]);
        store.delete("file", [...idx.filesByContent.keys(contentId)]);
        store.delete("link", [...idx.linksByContent.keys(contentId)]);
        store.delete("callRecord", [...idx.callRecordsByContent.keys(contentId)]);
      }
    }

    // Both callers prune from the oldest end, so the rows that survive are still contiguous but
    // the recorded floor now sits below all of them. Walk it forward to the oldest survivor, or
    // clear the span outright when nothing survives. Left alone, scroll-back would page from a
    // timestamp older than everything cached and skip the pruned range — the WEB-2197 gap,
    // reopened by eviction.
    //
    // Survivors can also sit entirely *above* the ceiling: realtime rows never folded into the
    // span. Walking the floor onto them would invert the span and claim untrusted rows are
    // trusted, so treat that case as nothing trusted surviving and clear the span.
    for (const feedId of feedIds) {
      let oldestSurviving: string | null = null;
      for (const itemId of idx.itemsByFeed.keys(feedId)) {
        const createdAt = store.get("item", itemId)?.createdAt;
        if (createdAt && (!oldestSurviving || new Date(createdAt).getTime() < new Date(oldestSurviving).getTime())) {
          oldestSurviving = createdAt;
        }
      }
      store.update("feed", feedId, (row) => {
        const ceiling = row.newestContiguousCreatedAt;
        const noTrustedSurvivor =
          !oldestSurviving || !ceiling || new Date(oldestSurviving).getTime() > new Date(ceiling).getTime();
        const oldestContiguousCreatedAt =
          row.oldestContiguousCreatedAt == null || noTrustedSurvivor
            ? null
            : new Date(oldestSurviving as string).getTime() > new Date(row.oldestContiguousCreatedAt).getTime()
              ? oldestSurviving
              : row.oldestContiguousCreatedAt;
        const newestContiguousCreatedAt = noTrustedSurvivor ? null : ceiling;
        if (
          !row.loadedFirstPage &&
          !row.loadedLastPage &&
          row.oldestContiguousCreatedAt === oldestContiguousCreatedAt &&
          row.newestContiguousCreatedAt === newestContiguousCreatedAt
        ) {
          return row;
        }
        // `loadedLastPage` durably skips the cold-open fetch (WEB-2254), so it cannot survive a
        // prune: the cache no longer holds the page that skip promises.
        return {
          ...row,
          loadedFirstPage: false,
          loadedLastPage: false,
          oldestContiguousCreatedAt,
          newestContiguousCreatedAt,
        };
      });
    }
  });

  log.debug(
    `[${label}] Complete: ${itemIds.length} items, ${orphanedContentIds.length} content records, ${feedIds.length} feeds`,
  );

  return {
    deletedItems: itemIds.length,
    deletedContentIds: orphanedContentIds.length,
    affectedFeeds: feedIds.length,
  };
}
